import Vue from 'vue'
import Router from 'vue-router'
import store from '@store/index'

import commonRoutes from './common-routes'
import authRoutes from './auth-routes'
import adminRoutes from './admin-routes'
import agencyRoutes from './agency-routes'
import agencyNewRoutes from './agency-new-routes'
import businessRoutes from './business-routes'
import businessNewRoutes from './business-new-routes'
import landingRoutes from "./landing-routes";
import teamRoutes from "./team-routes";
import { userUsesNewUiEligible } from '@utils/userUsesNewUiEligible'

Vue.use(Router)

const newBusinessToLegacy = {
  'new-business.dashboard': 'business.dashboard',
  'new-business.reviews': 'business.reviews',
  'new-business.employees': 'business.employees',
  'new-business.settings': 'business.settings',
  'new-business.social-posts': 'business.social-posts',
  'new-business.instagram-widget': 'business.instagram-widget',
  'new-business.review-landing-page': 'business.review-gate',
  'new-business.review-surge': 'business.review-contact-series',
  'new-business.review-sequence': 'business.review-contact-series',
  'new-business.review-images': 'business.review-images',
  'new-business.review-image-editor': 'business.review-image-editor',
  'new-business.request-contacts': 'business.request-contacts',
  'new-business.request-personalized-image': 'business.image-editor-dashboard',
  'new-business.request-review-widget': 'business.review-widgets',
}

const legacyBusinessToNew = Object.fromEntries(
  Object.entries(newBusinessToLegacy).map(([newName, legacyName]) => [legacyName, newName])
)

const newAgencyToLegacy = {
  'new-agency.dashboard': 'agency.dashboard',
  'new-agency.businesses.index': 'agency.businesses.index',
  'new-agency.customers.index': 'agency.customers.index',
  'new-agency.settings': 'agency.settings',
  'new-agency.embed': 'agency.embed',
  'new-agency.sr-widget': 'agency.sr-widget',
  'new-agency.zensocial-widget': 'agency.zensocial-widget',
  'new-agency.sf-widget': 'agency.sf-widget',
  'new-agency.instagram-widget.index': 'agency.instagram-widget.index',
  'new-agency.insta-stream-widget': 'agency.insta-stream-widget',
  'new-agency.widgets.index': 'agency.widgets.index',
  'new-agency.business-subscriptions': 'agency.business-subscriptions',
  'new-agency.subscriptions': 'agency.subscriptions',
}

const legacyAgencyToNew = Object.fromEntries(
  Object.entries(newAgencyToLegacy).map(([newName, legacyName]) => [legacyName, newName])
)

const createRouter = () => {
  const routes = [
    ...commonRoutes,
    ...authRoutes,
    ...adminRoutes,
    // Legacy /agency/* must register before new-agency routes: same paths (e.g. /agency/settings)
    // would otherwise always match NewDesign first when resolving by URL.
    ...agencyRoutes,
    ...agencyNewRoutes,
    ...businessRoutes,
    ...businessNewRoutes,
    ...landingRoutes,
    ...teamRoutes
  ]

  const router = new Router({
    mode: 'history',
    routes: routes,
  })

  return router
}

const router = createRouter()

Router.prototype.resetRouter = function() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher // the relevant part
}

router.beforeEach((to, from, next) => {
  const redirectNewUiRouteIfNotEligible = () => {
    const user = store.state.auth.user
    const toName = String(to.name || '')
    if (!user) return false

    const eligible = userUsesNewUiEligible(user)

    if (!eligible) {
      if (toName.startsWith('new-business.')) {
        const target = newBusinessToLegacy[toName] || 'business.dashboard'
        const route = { name: target, query: to.query, hash: to.hash }
        if (target === 'business.review-image-editor' && to.params && to.params.platform) {
          route.params = { platform: to.params.platform }
        }
        next(route)
        return true
      }

      if (toName.startsWith('new-agency.')) {
        const target = newAgencyToLegacy[toName] || 'agency.dashboard'
        next({ name: target, query: to.query, hash: to.hash })
        return true
      }
      return false
    }

    // Eligible users: prefer new routes for mapped legacy names.
    if (toName.startsWith('business.')) {
      const target = legacyBusinessToNew[toName]
      if (target && target !== toName) {
        const route = { name: target, query: to.query, hash: to.hash }
        if (target === 'new-business.review-image-editor' && to.params && to.params.platform) {
          route.params = { platform: to.params.platform }
        }
        next(route)
        return true
      }
    }

    if (toName.startsWith('agency.')) {
      const target = legacyAgencyToNew[toName]
      if (target && target !== toName) {
        next({ name: target, query: to.query, hash: to.hash })
        return true
      }
    }

    return false
  }

  if (store.getters['auth/isLoggedIn'] && !store.state.auth.user) {
    store
      .dispatch('auth/getProfile')
      .then((res) => {
        if (redirectNewUiRouteIfNotEligible()) return
        next()
      })
      .catch(() => {
        next()
      })
  } else {
    if (redirectNewUiRouteIfNotEligible()) return
    next()
  }
})

export default router
